import { Box, Icon, IconProps, Tooltip, styled } from '@mui/material';

import { TEXT_GRAY } from '../../constants/colors';
import { VisitPriority, VisitPriorityOrder, renderVisitPriority } from '../../types/visit';
import { makeTranslucent } from '../../utils/colors';

const PriorityIconContainer = styled('div')({
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    padding: '4px',
});

const PriorityBar = styled('div')(({ active }: { active: boolean }) => ({
    backgroundColor: active ? TEXT_GRAY : makeTranslucent(TEXT_GRAY, 0.4),
    height: '100%',
    width: '23%',
    borderRadius: '1.5px',
}));

export const VisitPriorityIcon = ({
    priority,
    ...rest
}: IconProps & { priority: VisitPriority }) => {
    return (
        <PriorityIcon
            priority={VisitPriorityOrder[priority]}
            values={Object.values(VisitPriorityOrder).sort((a, b) => a - b)}
            {...rest}
        />
    );
};

export const VisitPriorityIconWithTooltip = ({
    priority,
    ...rest
}: IconProps & { priority: VisitPriority }) => {
    return (
        <Tooltip title={`${renderVisitPriority(priority)} Priority`}>
            <Box>
                <VisitPriorityIcon priority={priority} {...rest} />
            </Box>
        </Tooltip>
    );
};

export const ServicePriorityIcon = ({ priority, ...rest }: IconProps & { priority: number }) => {
    return <PriorityIcon priority={priority} values={[1, 2, 3]} {...rest} />;
};

export const PriorityIcon = ({
    priority,
    values,
    ...rest
}: IconProps & { priority: number; values: number[] }) => {
    // Determine the range of values
    const sortedValues = values.sort();
    const minPriority = sortedValues[0];
    const maxPriority = sortedValues[sortedValues.length - 1];
    const midPriority = (minPriority + maxPriority) / 2;

    // Clamp the passed in value to the range of values
    let clampedPriority = Math.min(priority, sortedValues[sortedValues.length - 1]);
    clampedPriority = Math.max(clampedPriority, sortedValues[0]);

    return (
        <Icon {...rest}>
            <PriorityIconContainer>
                <PriorityBar active={clampedPriority >= minPriority} style={{ height: '40%' }} />
                <PriorityBar active={clampedPriority >= midPriority} style={{ height: '70%' }} />
                <PriorityBar active={clampedPriority >= maxPriority} style={{ height: '100%' }} />
            </PriorityIconContainer>
        </Icon>
    );
};
